import { AppProps } from "next/app";
import React, { useEffect, useState } from "react";
import Head from "next/head";
import { NextSeo } from "next-seo";
import getConfig from "next/config";
import Script from "next/script";

import { appCastJobView } from "utils/appcast";
import { getLinkTrackingProps, trackHeaderNavLinks, IOskar } from "utils/tracking";
import {
    initSnowplowTracker,
    trackSnowplowClicks,
    triggerSnowplowPageViewEvent,
    trackSnowplowModalActions
} from "@themuse/snowplow-js-client";

import "styles/globals.scss";

declare global {
    interface Window {
        OskarTS?: IOskar;
        gtag?: (...args: any[]) => void;
        dataLayer?: Record<string, any>;
        snowplow?: (...args: any[]) => void;
    }
}

const SnowplowInit = (jobObj: any) => {
    const [pageViewed, setPageViewed] = useState(false);
    const { publicRuntimeConfig } = getConfig();
    const { job } = jobObj;

    useEffect(() => {
        if (publicRuntimeConfig?.snowplowEndpoint && job && job.id && !pageViewed) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push(
                {
                    sp_page_section: "job_detail",
                    sp_page_is_external: false,
                    sp_page_sponsor_id: job?.company_id?.toString(),
                    sp_page_tab: null,
                    sp_page_type: "entity",
                    sp_page_entity_id: job?.id?.toString(),
                    sp_page_entity_uuid: job?.uuid?.toString(),
                }
            );
            initSnowplowTracker(publicRuntimeConfig.snowplowEndpoint);
            triggerSnowplowPageViewEvent();
            trackSnowplowClicks();
            trackSnowplowModalActions();
            setPageViewed(true);
        }
    }, [job]);

    return null;
};

const MyApp = ({ Component, pageProps }: AppProps) => {
    const { publicRuntimeConfig: { appEnv = "dev" } } = getConfig();
    const { initialAppParams } = pageProps;
    const { job } = initialAppParams || {};
    useEffect(() => {
        // eslint-disable-next-line quotes
        const header: HTMLElement = document.querySelector(
            // eslint-disable-next-line quotes
            `header[class^="Header_header"]`
        );
        header?.addEventListener("click", trackHeaderNavLinks, false);
        document.addEventListener("click", getLinkTrackingProps, false);
        document.addEventListener("contextmenu", getLinkTrackingProps, false);
        appCastJobView();
    }, []);

    return (
        <>
            <Head>
                {/* charset|viewport|http-equiv */}
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                {/* Title */}
                {/* Preconnect */}
                <link rel="preconnect" href="https://www.googletagmanager.com" />
                <link rel="preconnect" href="https://www.google.com" />
                <link rel="preconnect" href="https://www.google-analytics.com" />
                <link rel="preconnect" href="https://connect.facebook.net" />
                <link rel="preconnect" href="https://assets.themuse.com" />
                <link rel="preconnect" href="https://cdn.jwplayer.com" />
                <link rel="preconnect" href="https://public-assets.themuse.com" />
                <link rel="preconnect" href="https://surveys-static.survicate.com" />
                {/* CSS with import statements */}
                {/* Inline JS */}
                {/* Inline CSS */}
                {/* Synchronous JS */}
                {/* Synchronous CSS */}
                {/* preload */}
                <link
                    rel="preload"
                    as="font"
                    type="font/woff2"
                    href="https://public-assets.themuse.com/fonts/nunito-sans-v6-latin-regular.woff2"
                    crossOrigin="anonymous"
                />
                <link
                    rel="preload"
                    as="font"
                    type="font/woff2"
                    href="https://public-assets.themuse.com/fonts/nunito-sans-v6-latin-700.woff2"
                    crossOrigin="anonymous"
                />
                <link
                    rel="preload"
                    as="font"
                    type="font/woff2"
                    href="https://public-assets.themuse.com/fonts/nunito-sans-v6-latin-600.woff2"
                    crossOrigin="anonymous"
                />
                <link
                    rel="preload"
                    as="font"
                    type="font/woff2"
                    href="https://public-assets.themuse.com/fonts/barlow-semi-condensed-v6-latin-600.woff2"
                    crossOrigin="anonymous"
                />
                {/* prefetch & pre-renderer */}
                {/* Everything Else (SEO, Meta tags, icons, Open Graph...) */}
                <link
                    rel="apple-touch-icon"
                    sizes="180x180"
                    href="https://public-assets.themuse.com/images/favicons/apple-touch-icon.png"
                />
                <link
                    rel="icon"
                    type="image/png"
                    sizes="32x32"
                    href="https://public-assets.themuse.com/images/favicons/favicon-32x32.png"
                />
                <link
                    rel="icon"
                    type="image/png"
                    sizes="16x16"
                    href="https://public-assets.themuse.com/images/favicons/favicon-16x16.png"
                />
                <link
                    rel="manifest"
                    href="https://public-assets.themuse.com/images/favicons/site.webmanifest"
                />
                <link
                    rel="mask-icon"
                    href="https://public-assets.themuse.com/images/favicons/safari-pinned-tab.svg"
                    color="#9ADBF5"
                />
                <link
                    rel="shortcut icon"
                    href="https://public-assets.themuse.com/images/favicons/favicon.ico"
                />
                <meta name="apple-mobile-web-app-title" content="The Muse" />
                <meta name="application-name" content="The Muse" />
                <meta name="msapplication-TileColor" content="#9ADBF5" />
                <meta
                    name="msapplication-config"
                    content="https://public-assets.themuse.com/images/favicons/browserconfig.xml"
                />
                <meta name="theme-color" content="#9ADBF5" />
            </Head>
            <Script
                strategy="beforeInteractive"
                title="Oskar_Setup"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                    __html: `
                    window.OskarTS = window.OskarTS || {
                        q: []
                    };
                    for (
                        var d = function d(e) {
                                return function() {
                                    OskarTS.q.push([e].concat(Array.prototype.slice.call(arguments, 0)));
                                };
                            },
                            e = [
                                "setGlobalUserProperties",
                                "setGlobalEventProperties",
                                "capture",
                                "setEnvironment",
                                "getOskarID",
                                "getSessionID"
                            ],
                            f = 0; f < e.length; f++
                    ) {
                        OskarTS[e[f]] = d(e[f]);
                    }
                    OskarTS.setEnvironment(${JSON.stringify(appEnv)});
                    OskarTS.setGlobalEventProperties({
                        page_referrer: document.referrer,
                        query_string: window.location.search,
                        path: window.location.pathname
                    });
                    `
                }}
            />
            <Script
                strategy="beforeInteractive"
                title="GTM_Setup"
                key="GTM_Setup"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                    __html: `
                    window.dataLayer = window.dataLayer || [];
                    var jobId = "${job?.id}" !== "undefined" ? "${job?.id}" : null;
                    window.dataLayer.push({ page_id: jobId });
                    window.dataLayer.push({ "gtm.start": new Date().getTime(),event: "gtm.js" });
                    `
                }}
            />
            <Script
                strategy="beforeInteractive"
                title="GA_Setup"
                key="GA_Setup"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                    __html: `
                        window.ga=window.ga||function(){(ga.q=ga.q||[]).push(arguments)};ga.l=+new Date;
                        ga("create", "UA-29372519-1", "themuse.com", {"useAmpClientId": true});
                        ga("send", "pageview");
                    `
                }}
            />
            <Script
                strategy="lazyOnload"
                title="GA"
                key="GA"
                src="https://www.google-analytics.com/analytics.js"
            />
            <Script
                strategy="lazyOnload"
                id="Oskar"
                key="Oskar"
                src="https://public-assets.themuse.com/js/oskar.js"
            />
            <Script
                strategy="lazyOnload"
                title="GTM"
                key="GTM"
                src="https://www.googletagmanager.com/gtm.js?id=GTM-P3K3QB"
            />
            <NextSeo
                openGraph={{
                    type: "website",
                    site_name: "The Muse",
                    locale: "en_US",
                    images: [
                        {
                            url: "https://www.themuse.com/static/images/muse-og-image.png",
                            width: 400,
                            height: 400,
                            alt: "The Muse Logo",
                        },
                    ],
                }}
                twitter={{
                    site: "@TheMuse",
                    cardType: "summary_large_image",
                }}
                nofollow={false}
            />
            <SnowplowInit job={job} />
            <Component {...pageProps} />
        </>
    );
};

export default MyApp;
